/**
 * Self-test suite: Delete-for-everyone (tombstone)
 *
 * Covers two halves — mirrors iOS `TombstoneSelfTest.swift`:
 *
 *   A. Tombstone codec — pure (de)serialisation, server-spoofing resistance:
 *      A1. encode → decode round-trips (messageId, originalAuthorId, ts, schema)
 *      A2. isTombstone is true for our prefix; false for chat / envelope / receipt
 *      A3. tryDecodeTombstone returns null for non-tombstone plaintext
 *      A4. malformed base64 in tombstone prefix throws TombstoneCodecError malformed
 *      A5. wrong schema version throws TombstoneCodecError unsupportedSchema
 *      A6. empty messageId throws TombstoneCodecError malformed
 *      A7. empty originalAuthorId throws TombstoneCodecError malformed
 *      A8. tombstone wire-form prefix does not collide with receipt prefix
 *
 *   B. TombstoneStore — author authentication, monotonicity, persistence:
 *      B1. record(matching author) writes the tombstone, isDeleted === true
 *      B2. record(third-party requester) throws TombstoneAuthorMismatchError, no state written
 *      B3. duplicate tombstone is idempotent (observedAt not churned)
 *      B4. multiple messages tracked independently
 *      B5. clear(messageId) is scoped to one message
 *      B6. resetAll() drops every tombstone
 *      B7. persistence: write → reopen DB (fresh instance, same dbName) → state preserved
 *      B8. observedAt uses local clock; claimedTs preserves peer claim
 *      B9. tombstone is monotonic across replay / hostile re-send
 *
 * All cases use isolated, uniquely-named IndexedDB databases so the suite
 * is safe on every dev launch and cannot pollute the production database.
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  makeTombstone,
  encodeTombstone,
  decodeTombstone,
  tryDecodeTombstone,
  isTombstone,
  TOMBSTONE_PREFIX,
  TOMBSTONE_CURRENT_SCHEMA,
  TombstoneCodecError,
} from "./Tombstone";
import { TombstoneStore, TombstoneAuthorMismatchError } from "./TombstoneStore";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Create a fresh TombstoneStore backed by a unique throwaway DB. */
function freshStore(): TombstoneStore {
  return new TombstoneStore(`kaether-selftest-tomb-${crypto.randomUUID()}`);
}

/** Same dbName store pair — used for the persistence case. */
function namedStores(): { dbName: string; writer: TombstoneStore; reader: () => TombstoneStore } {
  const dbName = `kaether-selftest-tomb-${crypto.randomUUID()}`;
  return {
    dbName,
    writer: new TombstoneStore(dbName),
    reader: () => new TombstoneStore(dbName),
  };
}

/** Build a base64url payload string from a raw JSON string (test fixture). */
function b64url(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
}

// ---------------------------------------------------------------------------
// Codec test cases
// ---------------------------------------------------------------------------

// A1. Encode → decode round-trip preserves all fields.
async function testCodecRoundTrip(): Promise<TestCase> {
  const name = "codec round-trips tombstone";
  try {
    const t = makeTombstone("msg-1", "alice", 1_700_000_000);
    const encoded = encodeTombstone(t);
    const decoded = decodeTombstone(encoded);
    if (
      decoded.schema !== t.schema ||
      decoded.messageId !== t.messageId ||
      decoded.originalAuthorId !== t.originalAuthorId ||
      decoded.ts !== t.ts
    ) {
      return fail(name, `got ${JSON.stringify(decoded)}, want ${JSON.stringify(t)}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// A2. isTombstone distinguishes from chat, envelope, receipt text.
async function testIsTombstoneDetection(): Promise<TestCase> {
  const name = "isTombstone distinguishes from chat/envelope/receipt text";
  try {
    const t = makeTombstone("abc", "alice");
    const encoded = encodeTombstone(t);
    const positive = isTombstone(encoded);
    const negativeUserMessage = isTombstone("hello world");
    const negativeEnvelope = isTombstone("kaether:e2ee:v1:foo");
    const negativeReceipt = isTombstone("kaether:receipt:v1:abc");
    const ok =
      positive && !negativeUserMessage && !negativeEnvelope && !negativeReceipt;
    if (!ok) {
      return fail(
        name,
        `pos=${positive} neg_user=${negativeUserMessage} neg_env=${negativeEnvelope} neg_rcpt=${negativeReceipt}`,
      );
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// A3. tryDecodeTombstone returns null for a plain user message.
async function testTryDecodeReturnsNullForUserMessage(): Promise<TestCase> {
  const name = "tryDecodeTombstone returns null for user messages";
  try {
    const result = tryDecodeTombstone("just a chat message");
    if (result !== null) {
      return fail(name, `got ${JSON.stringify(result)}, want null`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// A4. Malformed base64 in tombstone prefix → malformed error.
async function testMalformedBase64Rejected(): Promise<TestCase> {
  const name = "malformed base64 in tombstone is rejected";
  const bad = TOMBSTONE_PREFIX + "@@@not-base64@@@";
  try {
    decodeTombstone(bad);
    return fail(name, "decode did not throw");
  } catch (e) {
    if (e instanceof TombstoneCodecError && e.info.kind === "malformed") {
      return pass(name);
    }
    return fail(name, `wrong error: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// A5. Unsupported schema version → unsupportedSchema error.
async function testUnsupportedSchemaRejected(): Promise<TestCase> {
  const name = "unsupported schema version is rejected";
  const json = `{"schema":99,"messageId":"abc","originalAuthorId":"alice","ts":1}`;
  const payload = TOMBSTONE_PREFIX + b64url(json);
  try {
    decodeTombstone(payload);
    return fail(name, "decode did not throw");
  } catch (e) {
    if (
      e instanceof TombstoneCodecError &&
      e.info.kind === "unsupportedSchema" &&
      e.info.schema === 99
    ) {
      return pass(name);
    }
    return fail(name, `wrong error: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// A6. Empty messageId → malformed error.
async function testEmptyMessageIdRejected(): Promise<TestCase> {
  const name = "empty messageId is rejected";
  const json = `{"schema":${TOMBSTONE_CURRENT_SCHEMA},"messageId":"","originalAuthorId":"alice","ts":1}`;
  const payload = TOMBSTONE_PREFIX + b64url(json);
  try {
    decodeTombstone(payload);
    return fail(name, "decode did not throw");
  } catch (e) {
    if (e instanceof TombstoneCodecError && e.info.kind === "malformed") {
      return pass(name);
    }
    return fail(name, `wrong error: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// A7. Empty originalAuthorId → malformed error.
async function testEmptyAuthorIdRejected(): Promise<TestCase> {
  const name = "empty originalAuthorId is rejected";
  const json = `{"schema":${TOMBSTONE_CURRENT_SCHEMA},"messageId":"abc","originalAuthorId":"","ts":1}`;
  const payload = TOMBSTONE_PREFIX + b64url(json);
  try {
    decodeTombstone(payload);
    return fail(name, "decode did not throw");
  } catch (e) {
    if (e instanceof TombstoneCodecError && e.info.kind === "malformed") {
      return pass(name);
    }
    return fail(name, `wrong error: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// A8. Tombstone and receipt prefix do not collide.
async function testTombstoneAndReceiptPrefixesAreDistinct(): Promise<TestCase> {
  const name = "tombstone and receipt prefixes do not collide";
  try {
    const tombstoneEnc = encodeTombstone(makeTombstone("m", "a"));
    const receiptEnc = "kaether:receipt:v1:" + b64url(`{"schema":1,"messageId":"m","type":"delivered","ts":1}`);
    const ok =
      isTombstone(tombstoneEnc) &&
      !isTombstone(receiptEnc) &&
      receiptEnc.startsWith("kaether:receipt:v1:") &&
      !tombstoneEnc.startsWith("kaether:receipt:v1:");
    if (!ok) {
      return fail(
        name,
        `tombstoneEnc=${tombstoneEnc.slice(0, 40)} receiptEnc=${receiptEnc.slice(0, 40)}`,
      );
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// ---------------------------------------------------------------------------
// Store test cases
// ---------------------------------------------------------------------------

// B1. record(matching author) → isDeleted = true.
async function testRecordWithMatchingAuthor(): Promise<TestCase> {
  const name = "record(matching author) marks message deleted";
  const s = freshStore();
  try {
    const t = makeTombstone("m1", "alice");
    await s.record(t, "alice");
    const deleted = await s.isDeleted("m1");
    if (!deleted) {
      return fail(name, "isDeleted returned false after successful record");
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// B2. record(third-party requester) → throws TombstoneAuthorMismatchError, no state written.
async function testRecordWithThirdPartyRejected(): Promise<TestCase> {
  const name = "record(third-party requester) is rejected with TombstoneAuthorMismatchError";
  const s = freshStore();
  try {
    const t = makeTombstone("m1", "alice");
    try {
      await s.record(t, "mallory");
      return fail(name, "record did not throw for author mismatch");
    } catch (e) {
      if (!(e instanceof TombstoneAuthorMismatchError)) {
        return fail(name, `wrong error type: ${e instanceof Error ? e.message : String(e)}`);
      }
      // Verify claimedAuthor / requestedBy are populated correctly.
      if (e.claimedAuthor !== "alice" || e.requestedBy !== "mallory") {
        return fail(
          name,
          `claimedAuthor=${e.claimedAuthor} requestedBy=${e.requestedBy}, expected alice/mallory`,
        );
      }
    }
    // Store must not have persisted anything.
    const deleted = await s.isDeleted("m1");
    if (deleted) {
      return fail(name, "isDeleted is true after rejected record — state was written");
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw unexpected: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// B3. Duplicate tombstone is idempotent — observedAt not churned.
async function testDuplicateRecordIsIdempotent(): Promise<TestCase> {
  const name = "duplicate tombstone does not churn observedAt";
  const s = freshStore();
  try {
    const t = makeTombstone("m1", "alice", 100);
    const firstObserved = new Date("2024-01-01T00:00:01Z");
    const secondObserved = new Date("2999-12-31T00:00:00Z");

    await s.record(t, "alice", firstObserved);
    const r1 = await s.get("m1");
    await s.record(t, "alice", secondObserved);
    const r2 = await s.get("m1");

    if (!r1 || !r2) {
      return fail(name, `record returned undefined: r1=${JSON.stringify(r1)} r2=${JSON.stringify(r2)}`);
    }
    if (r1.observedAt !== r2.observedAt) {
      return fail(name, `observedAt changed: ${r1.observedAt} → ${r2.observedAt}`);
    }
    if (r1.observedAt !== firstObserved.toISOString()) {
      return fail(name, `observedAt not the first observation: got ${r1.observedAt}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// B4. Multiple messages tracked independently.
async function testMultipleMessagesTrackedIndependently(): Promise<TestCase> {
  const name = "multiple tombstones tracked independently";
  const s = freshStore();
  try {
    await s.record(makeTombstone("m1", "alice"), "alice");
    await s.record(makeTombstone("m2", "bob"), "bob");
    const d1 = await s.isDeleted("m1");
    const d2 = await s.isDeleted("m2");
    const d3 = await s.isDeleted("m3");
    if (!d1 || !d2 || d3) {
      return fail(name, `m1=${d1} m2=${d2} m3=${d3}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// B5. clear(messageId) is scoped to one message.
async function testClearOneMessageDoesNotAffectOthers(): Promise<TestCase> {
  const name = "clear(messageId) is scoped to one message";
  const s = freshStore();
  try {
    await s.record(makeTombstone("m1", "alice"), "alice");
    await s.record(makeTombstone("m2", "bob"), "bob");
    await s.clear("m1");
    const d1 = await s.isDeleted("m1");
    const d2 = await s.isDeleted("m2");
    if (d1 || !d2) {
      return fail(name, `m1=${d1} (want false) m2=${d2} (want true)`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// B6. resetAll() clears every tombstone.
async function testResetAll(): Promise<TestCase> {
  const name = "resetAll() clears every tombstone";
  const s = freshStore();
  try {
    await s.record(makeTombstone("m1", "alice"), "alice");
    await s.record(makeTombstone("m2", "bob"), "bob");
    await s.resetAll();
    const d1 = await s.isDeleted("m1");
    const d2 = await s.isDeleted("m2");
    if (d1 || d2) {
      return fail(name, `m1=${d1} m2=${d2} (both want false)`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// B7. Persistence: state survives a fresh store instance on the same DB.
async function testPersistenceAcrossInstances(): Promise<TestCase> {
  const name = "tombstone state persists across store instances";
  const { writer, reader } = namedStores();
  try {
    await writer.record(makeTombstone("m1", "alice"), "alice");

    // Open a brand-new instance on the same DB (simulates app restart).
    const readerInst = reader();
    const recovered = await readerInst.get("m1");
    const deleted = await readerInst.isDeleted("m1");

    if (!recovered || recovered.originalAuthorId !== "alice" || !deleted) {
      return fail(
        name,
        `recovered=${JSON.stringify(recovered)} isDeleted=${deleted}`,
      );
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// B8. observedAt uses local clock; claimedTs preserves peer's claim.
async function testObservedAtIsLocalClock(): Promise<TestCase> {
  const name = "observedAt uses local clock; claimedTs preserves peer claim";
  const s = freshStore();
  try {
    const peerLies = 9_999_999_999;
    const weObserved = new Date("2023-11-14T22:13:20.000Z"); // 2023-11-14 ≈ 1_700_000_000
    await s.record(
      makeTombstone("m1", "alice", peerLies),
      "alice",
      weObserved,
    );
    const r = await s.get("m1");
    if (!r) return fail(name, "get returned undefined");
    if (r.claimedTs !== peerLies) {
      return fail(name, `claimedTs=${r.claimedTs}, want ${peerLies}`);
    }
    if (r.observedAt !== weObserved.toISOString()) {
      return fail(name, `observedAt=${r.observedAt}, want ${weObserved.toISOString()}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// B9. Tombstone is monotonic — hostile replay / re-send cannot undo it.
async function testTombstoneIsMonotonic(): Promise<TestCase> {
  const name = "tombstone is monotonic across replay and hostile re-send";
  const s = freshStore();
  try {
    await s.record(makeTombstone("m1", "alice"), "alice");

    // Alice sends the tombstone again — no-op.
    await s.record(makeTombstone("m1", "alice"), "alice");

    // Mallory tries to send a tombstone for alice's message but will be
    // rejected (throws). The tombstone should still be in place.
    let mismatchThrown = false;
    try {
      await s.record(makeTombstone("m1", "alice"), "mallory");
    } catch (e) {
      if (e instanceof TombstoneAuthorMismatchError) mismatchThrown = true;
    }
    if (!mismatchThrown) {
      return fail(name, "hostile record(mallory) did not throw TombstoneAuthorMismatchError");
    }

    const still = await s.isDeleted("m1");
    if (!still) {
      return fail(name, "tombstone was removed after hostile replay");
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Delete-for-everyone (tombstone)",
  isVerified: true,
  run: async () =>
    buildSuiteResult([
      // Codec
      await testCodecRoundTrip(),
      await testIsTombstoneDetection(),
      await testTryDecodeReturnsNullForUserMessage(),
      await testMalformedBase64Rejected(),
      await testUnsupportedSchemaRejected(),
      await testEmptyMessageIdRejected(),
      await testEmptyAuthorIdRejected(),
      await testTombstoneAndReceiptPrefixesAreDistinct(),
      // Store
      await testRecordWithMatchingAuthor(),
      await testRecordWithThirdPartyRejected(),
      await testDuplicateRecordIsIdempotent(),
      await testMultipleMessagesTrackedIndependently(),
      await testClearOneMessageDoesNotAffectOthers(),
      await testResetAll(),
      await testPersistenceAcrossInstances(),
      await testObservedAtIsLocalClock(),
      await testTombstoneIsMonotonic(),
    ]),
});
